/**
 * @fileoverview Message Service - Error Message Processing
 *
 * Provides intelligent error message handling with multiple fallback strategies:
 * 1. Error code mapping (preferred - future-proof)
 * 2. Pattern matching (fallback - pragmatic for legacy backends)
 * 3. Raw message extraction (last resort - graceful degradation)
 *
 * This service bridges the gap between backend error formats and user-friendly
 * i18n-ready frontend messages.
 *
 * @author Engineering Team
 * @copyright Cirrus Invicta 2025
 * @private
 * @version 1.0.0
 */

import { logger } from '@/utils/logger'

interface ErrorPattern {
    match: RegExp
    i18nKey: string
}

/**
 * Message Service - Transform backend errors into i18n keys or user-friendly messages
 */
export class MessageService {
    /**
     * Process API error with guaranteed string return (public API)
     *
     * This is the primary method most code should call. It guarantees a string return
     * by falling back to the provided message when extraction fails.
     *
     * Strategy:
     * 1. Try error code mapping (preferred - future-proof)
     * 2. Try pattern matching on text (fallback - pragmatic)
     * 3. Return raw message (if available)
     * 4. Return fallback (last resort)
     *
     * @param error - Error response data from backend
     * @param fallback - Fallback message if extraction fails
     * @returns User-friendly error message (never null)
     */
    static processApiError(error: unknown, fallback: string): string {
        const result = this.processError(error)
        return result || fallback
    }

    /**
     * Process error and extract message (pure extraction)
     *
     * This is the internal method that performs pure message extraction.
     * Returns null when no message can be confidently extracted.
     * Most code should call processApiError() instead.
     *
     * @param errorData - Error response data from backend
     * @returns Extracted message or null
     */
    static processError(errorData: unknown): string | null {
        if (!errorData || typeof errorData !== 'object') {
            return null
        }

        const data = errorData as Record<string, unknown>

        // Strategy 1: Error code mapping (PREFERRED - future-proof)
        if (data.code && typeof data.code === 'string') {
            const i18nKey = this.mapErrorCode(data.code)
            if (i18nKey) {
                logger.debug('MessageService: Mapped error code to i18n key', {
                    code: data.code,
                    i18nKey
                })
                return i18nKey
            }
        }

        // Strategy 2: Pattern matching on text (FALLBACK - pragmatic)
        const message = this.extractMessageText(data)
        if (message) {
            const i18nKey = this.matchErrorPattern(message)
            if (i18nKey) {
                logger.debug('MessageService: Matched error pattern to i18n key', {
                    pattern: message.substring(0, 50),
                    i18nKey
                })
                return i18nKey
            }

            // No pattern matched - return raw message
            logger.debug('MessageService: Using raw backend message', {
                message: message.substring(0, 50)
            })
            return message
        }

        return null
    }

    /**
     * Map backend error codes to i18n keys
     * Preferred strategy - future-proof and i18n-friendly
     *
     * @param code - Backend error code
     * @returns i18n key or null if not mapped
     */
    private static mapErrorCode(code: string): string | null {
        const codeMap: Record<string, string> = {
            // Domain errors
            DOMAIN_EXISTS: 'errors.domain.alreadyExists',
            INVALID_FQDN: 'errors.domain.invalidFormat',
            DOMAIN_INVALID_FORMAT: 'errors.domain.invalidFormat',
            DOMAIN_NOT_FOUND: 'errors.domain.notFound',

            // Validation errors
            UNIQUE_CONSTRAINT: 'errors.validation.mustBeUnique',
            INVALID_CHOICE: 'errors.validation.invalidSelection',
            REQUIRED_FIELD: 'errors.validation.required',

            // Authentication/Authorization
            PERMISSION_DENIED: 'errors.auth.permissionDenied',
            UNAUTHORIZED: 'errors.auth.unauthorized',
            SESSION_EXPIRED: 'errors.auth.sessionExpired',

            // Organization errors
            ORG_EXISTS: 'errors.organization.alreadyExists',
            ORG_NOT_FOUND: 'errors.organization.notFound'

            // Add more error codes as backend implements them
        }

        return codeMap[code] || null
    }

    /**
     * Pattern matching on error text
     * Fallback strategy for backends that don't send error codes yet
     *
     * NOTE: This is TEMPORARY until backend sends structured error codes.
     * Patterns map to i18n keys (not hardcoded English text).
     *
     * @param text - Error message text from backend
     * @returns i18n key or null if no pattern matches
     */
    private static matchErrorPattern(text: string): string | null {
        // Patterns ported from old errorMessages.js
        // Maps backend text patterns to i18n keys
        const patterns: ErrorPattern[] = [
            {
                match: /name, organization must make a unique set/i,
                i18nKey: 'errors.domain.alreadyExistsInOrg'
            },
            {
                match: /unique/i,
                i18nKey: 'errors.validation.mustBeUnique'
            },
            {
                match: /not a valid choice/i,
                i18nKey: 'errors.validation.invalidSelection'
            },
            {
                match: /is not a valid FQDN/i,
                i18nKey: 'errors.domain.invalidFormat'
            },
            {
                match: /permission denied/i,
                i18nKey: 'errors.auth.permissionDenied'
            }
            // Add more patterns as needed
        ]

        for (const pattern of patterns) {
            if (pattern.match.test(text)) {
                return pattern.i18nKey
            }
        }

        return null // No pattern matched
    }

    /**
     * Extract message text from various error response structures
     *
     * @param data - Error response data
     * @returns Extracted message or null
     */
    private static extractMessageText(data: Record<string, unknown>): string | null {
        // Check common error message fields
        const candidates = [data.detail, data.message, data.error]

        for (const candidate of candidates) {
            if (typeof candidate === 'string' && candidate.trim()) {
                return candidate.trim()
            }
        }

        return null
    }
}
